import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { InvestmentRoundingSetting } from 'shared';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { SafeHooks, useReactForm } from '../../../hooks/index';
import { Safe } from '../../../types/safes.types';
import { handleFormInvalid } from '../../../utils/handle-form-invalid.util';
import { toBackendDateFormat } from '../../../utils/to-backend-date-format.util';
import { useSafeConversionProgressStore } from './conversion-progress.store';
import { ChildrenFormProps } from './convert-modal.types';
import { FormSchema, formSchema } from './convert-modal.validation';
import { FinalStep, StepOne, StepThree, StepTwo } from './steps/index';

export type ConvertSafeModalProps = {
  companyId: string;
  conversionId: string;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  selectedSafeIds: string[];
  selectedSafes: Safe[];
};

const formSteps: ((props: ChildrenFormProps) => ReactNode)[] = [
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <StepThree {...props} />,
  (props) => <FinalStep {...props} />,
];

export const ConvertSafeModal: FC<ConvertSafeModalProps> = ({
  isOpenModal,
  companyId,
  handleClose,
  invalidateQuery,
  selectedSafes,
  selectedSafeIds,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1);
  const selectedSafesForConversion =
    selectedSafes.length > 0
      ? selectedSafes.map(({ id, stakeholder, investment, issueDate, floorValue }) => ({
          id,
          stakeholder: {
            fullName: stakeholder.fullName,
            id: stakeholder.id,
          },
          investment,
          issueDate: new Date(issueDate),
          floorValue,
        }))
      : [];

  // const { safeConversion } = SafeHooks.useSafeConversion({ companyId, conversionId });

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    getValues,
    getFieldState,
    setError,
    clearErrors,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
    defaultValues: {
      stepOne: {
        safes: selectedSafesForConversion,
        safeIds: selectedSafeIds,
        roundingSetting: InvestmentRoundingSetting.DEFAULT,
      },
    },
  });

  // useEffect(() => {
  //   if (!safeConversion) return;

  //   reset({
  //     stepOne: {
  //       conversionDate: safeConversion.conversionDate,
  //       linkedEvent: safeConversion.valuation
  //         ? {
  //             id: safeConversion.valuation.id,
  //             issuedShares:
  //               toNumber(safeConversion.valuation.issuedShares) - safeConversion.convertedShares,
  //             issuedSharesOnStart: safeConversion.valuation.issuedSharesOnStart,
  //             name: safeConversion.valuation.name,
  //             sharePrice: safeConversion.preConversionSharePrice,
  //           }
  //         : {},
  //       safeIds: safeConversion.items.map(({ id }) => id),
  //       shareClass: safeConversion.shareClass,
  //       safes: safeConversion.items,
  //       conversionSummary: {
  //         convertedShares: safeConversion.convertedShares,
  //         investment: safeConversion.items.reduce((acc, { investment }) => acc + investment, 0),
  //         preMoneyValuation:
  //           toNumber(safeConversion?.valuation?.issuedSharesOnStart) *
  //           safeConversion.preConversionSharePrice,
  //         sharePrice: safeConversion.preConversionSharePrice,
  //         totalOwned: safeConversion.convertedShares,
  //       },
  //     },
  //     stepThree: safeConversion,
  //   });
  // }, [safeConversion, reset]);

  const { convert, isPending } = SafeHooks.useConvertSafes();
  const setConverting = useSafeConversionProgressStore((s) => s.setConverting);

  const handleCloseModal = useCallback(() => {
    reset();
    handleClose();
    setCurrentStep(1);
  }, [reset, handleClose]);

  const submitHandler = useCallback(
    (data: FormSchema) => {
      const { stepOne, stepThree } = data;

      const safes = stepOne.safes.map(({ id, files }) => ({
        id,
        filesLinks: files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
      }));

      convert(
        {
          companyId,
          data: {
            safes,
            shareClassId: stepOne.shareClass.id,
            valuationId:
              stepOne.linkedEvent.id === 'no-valuation' ? undefined : stepOne.linkedEvent.id,
            conversionDate: toBackendDateFormat(stepOne.conversionDate),
            roundingSetting: stepOne.roundingSetting,
            additionalNotes: stepThree?.additionalNotes,
            filesLinks:
              stepThree?.files?.reduce<string[]>((prev, curr) => [...prev, curr.docLink], []) || [],
          },
        },
        {
          onSuccess: () => {
            const convertingSafeIds = stepOne.safeIds ?? [];
            if (convertingSafeIds.length > 0) setConverting(convertingSafeIds);
            toast.success('Conversion in progress. The page will update when it’s complete.');
            invalidateQuery();
            handleCloseModal();
          },
        },
      );
    },
    [companyId, convert, handleCloseModal, invalidateQuery, setConverting],
  );

  const handleNextStep = useCallback(() => {
    const nextStep = currentStep + 1;
    if (nextStep === Object.keys(formSteps).length + 1) {
      handleSubmit(submitHandler, handleFormInvalid)();
      return;
    }
    if (nextStep > Object.keys(formSteps).length) return;

    setCurrentStep(nextStep);
  }, [currentStep, handleSubmit, submitHandler]);

  const handlePrevStep = useCallback(() => {
    const prevStep = currentStep - 1;
    if (prevStep < 1) return;
    setCurrentStep(prevStep);
  }, [currentStep]);

  useEffect(() => {
    if (selectedSafes.length > 0 && selectedSafeIds.length > 0 && currentStep === 1) {
      const selectedSafesForConversion = selectedSafes.map(
        ({ id, stakeholder, investment, issueDate, floorValue }) => ({
          id,
          stakeholder: {
            fullName: stakeholder.fullName,
            id: stakeholder.id,
          },
          investment,
          issueDate: new Date(issueDate),
          floorValue,
        }),
      );

      setValue('stepOne.safes', selectedSafesForConversion);
      setValue('stepOne.safeIds', selectedSafeIds);
    }
  }, [selectedSafes, selectedSafeIds, setValue, currentStep]);

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="text-xl font-[550] text-gray-700">
              <AppFormattedMessage id="CONVERT_SAFE_TO_SHARES" />
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>
          <div className="flex w-full items-center gap-[5px] bg-white px-6 pb-6">
            {formSteps.map((_, i) => (
              <div
                className={twMerge(
                  'h-1 w-full rounded-[25px] transition-colors duration-300 ease-out',
                  i + 1 <= currentStep ? 'bg-[#12B76A]' : 'bg-gray-100',
                  i + 1 === currentStep &&
                    currentStep === formSteps.length &&
                    'bg-gradient-to-r from-forest-500 to-forest-300',
                )}
                key={`${i}_${companyId}`}
              />
            ))}
          </div>

          {/* Steps swap instantly, as in every other step modal. A crossfade between two forms
              read as the modal blinking whichever way it was timed (QA 1248h535xf0). */}
          <div className="flex h-full flex-col gap-4 overflow-hidden">
            {formSteps[currentStep - 1]({
              companyId,
              formData: getValues,
              nextFormStep: handleNextStep,
              prevFormStep: handlePrevStep,
              setFormData: setValue,
              control,
              clearErrors,
              handleCloseModal,
              setError,
              filedState: getFieldState,
              errors,
              isLoading: isPending,
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
